import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import {
  TrendingDown,
  Bell,
  BarChart3,
  Check,
  ArrowRight,
  DollarSign,
  Gift,
  Home,
  Zap,
  Target,
  Sparkles,
} from 'lucide-react';
import Button from 'shared/components/ui/Button';
import { Card } from 'shared/components/ui/Card';
import PublicWrapper from 'shared/components/layout/PublicWrapper';
import { URLS } from 'configs';
import './PriceTrackingPage.scss';

const PriceTrackingPage = () => {
  const { t } = useTranslation();
  const [activeMonth, setActiveMonth] = useState(3);

  // Mock price history data for demo
  const priceHistory = [
    { month: 'Jan', price: 299 },
    { month: 'Feb', price: 279 },
    { month: 'Mar', price: 249 },
    { month: 'Apr', price: 229 },
    { month: 'May', price: 199 },
    { month: 'Jun', price: 189 },
  ];

  const features = [
    {
      icon: Bell,
      title: t('price_feature1_title', 'Instant Price Alerts'),
      desc: t('price_feature1_desc', 'Get notified via email, push notification, or SMS when prices drop'),
      list: [
        t('price_feature1_1', 'Custom price targets'),
        t('price_feature1_2', 'Daily automatic checks'),
        t('price_feature1_3', 'Multi-store tracking'),
      ],
    },
    {
      icon: BarChart3,
      title: t('price_feature2_title', 'Historical Price Charts'),
      desc: t('price_feature2_desc', 'See price trends over time to make smarter buying decisions'),
      list: [
        t('price_feature2_1', '6 months of data'),
        t('price_feature2_2', 'Best time to buy'),
        t('price_feature2_3', 'Price predictions'),
      ],
    },
    {
      icon: TrendingDown,
      title: t('price_feature3_title', 'Deal Discovery'),
      desc: t('price_feature3_desc', 'AI finds better prices across multiple retailers automatically'),
      list: [
        t('price_feature3_1', 'Cross-store comparison'),
        t('price_feature3_2', 'Similar products'),
        t('price_feature3_3', 'Alternative options'),
      ],
    },
  ];

  const howItWorksSteps = [
    {
      step: '1',
      title: t('price_step1_title', 'Save Items to Wishlist'),
      desc: t('price_step1_desc', 'Add products from any online store using our browser extension'),
    },
    {
      step: '2',
      title: t('price_step2_title', 'Automatic Monitoring'),
      desc: t('price_step2_desc', 'We check prices daily across multiple retailers automatically'),
    },
    {
      step: '3',
      title: t('price_step3_title', 'Get Instant Alerts'),
      desc: t('price_step3_desc', 'Receive notifications when prices drop to your target'),
    },
    {
      step: '4',
      title: t('price_step4_title', 'Buy at Best Price'),
      desc: t('price_step4_desc', 'Purchase when the price is right with confidence'),
    },
  ];

  const useCases = [
    {
      icon: DollarSign,
      title: t('price_use1_title', 'Budget Shoppers'),
      desc: t('price_use1_desc', 'Maximize savings on every purchase'),
    },
    {
      icon: Gift,
      title: t('price_use2_title', 'Holiday Shopping'),
      desc: t('price_use2_desc', 'Track gifts and buy at best prices'),
    },
    {
      icon: Home,
      title: t('price_use3_title', 'Big Purchases'),
      desc: t('price_use3_desc', 'Monitor expensive items over time'),
    },
    {
      icon: Zap,
      title: t('price_use4_title', 'Deal Hunters'),
      desc: t('price_use4_desc', 'Never miss flash sales again'),
    },
  ];

  return (
    <PublicWrapper>
      <Helmet>
        <title>Smart Price Tracking | Never Miss a Deal | Likiee</title>
        <meta
          name='description'
          content='Automatic price tracking for all your wishlist items. Get alerts when prices drop, see historical price charts, and save $450+ per year. Never overpay again.'
        />
        <meta
          name='keywords'
          content='price tracking, deal alerts, price drop notifications, price history, smart shopping, save money, best deals'
        />
      </Helmet>
      <main className='price-tracking-page'>
        {/* Hero Section */}
        <section className='price-tracking-page__hero'>
          <div className='container'>
            {/* Decorative circles */}
            <div className='price-tracking-page__hero-decoration'>
              <div className='price-tracking-page__circle price-tracking-page__circle--1' />
              <div className='price-tracking-page__circle price-tracking-page__circle--2' />
            </div>

            <div className='price-tracking-page__hero-content'>
              {/* Badge */}
              <div className='price-tracking-page__badge'>
                <TrendingDown className='price-tracking-page__badge-icon' />
                <span className='price-tracking-page__badge-text'>{t('price_badge', 'Smart Tracking')}</span>
              </div>

              <h1 className='price-tracking-page__hero-title'>
                {t('price_hero_title_1', 'Never Miss a')}
                <span className='price-tracking-page__hero-title--gradient'>
                  {' '}
                  {t('price_hero_title_2', 'Price Drop')}
                </span>{' '}
                {t('price_hero_title_3', 'Again')}
              </h1>
              <p className='price-tracking-page__hero-description'>
                {t(
                  'price_hero_description',
                  'Automatic price tracking for all your wishlist items. Get instant alerts when prices drop, see historical trends, and save hundreds every year.',
                )}
              </p>
              <div className='price-tracking-page__hero-actions'>
                <a href={URLS.CONTACT} className='price-tracking-page__cta-link'>
                  <Button className='button-hero'>
                    <TrendingDown />
                    {t('contact_us', 'Contact us')}
                    <ArrowRight />
                  </Button>
                </a>
              </div>

              {/* Stats */}
              <div className='price-tracking-page__stats'>
                <div className='price-tracking-page__stat'>
                  <div className='price-tracking-page__stat-value'>$450</div>
                  <div className='price-tracking-page__stat-label'>{t('price_stat1', 'Avg. Yearly Savings')}</div>
                </div>
                <div className='price-tracking-page__stat'>
                  <div className='price-tracking-page__stat-value'>89%</div>
                  <div className='price-tracking-page__stat-label'>{t('price_stat2', 'Catch Price Drops')}</div>
                </div>
                <div className='price-tracking-page__stat'>
                  <div className='price-tracking-page__stat-value'>24/7</div>
                  <div className='price-tracking-page__stat-label'>{t('price_stat3', 'Automatic Monitoring')}</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Interactive Demo Section */}
        <section className='price-tracking-page__demo'>
          <div className='container'>
            <div className='price-tracking-page__demo-header'>
              <div className='price-tracking-page__demo-badge'>
                <Sparkles size={14} />
                <span>{t('price_demo_badge', 'Live Demo')}</span>
              </div>
              <h2 className='price-tracking-page__demo-title'>
                {t('price_demo_title', 'Watch Prices Drop in Real-Time')}
              </h2>
              <p className='price-tracking-page__demo-description'>
                {t('price_demo_desc', 'See how our AI tracks price changes and alerts you at the perfect moment')}
              </p>
            </div>

            <div className='price-tracking-page__demo-card'>
              {/* Product Info */}
              <div className='price-tracking-page__demo-product'>
                <div className='price-tracking-page__demo-product-image'>
                  <img src='https://images.unsplash.com/photo-1491553895911-0055eca6402d?w=300' alt='Sneakers' />
                </div>
                <div className='price-tracking-page__demo-product-info'>
                  <h3>{t('price_demo_product', 'Designer Running Shoes')}</h3>
                  <div className='price-tracking-page__demo-prices'>
                    <span className='price-tracking-page__demo-price-old'>$299</span>
                    <span className='price-tracking-page__demo-price-current'>$189</span>
                    <span className='price-tracking-page__demo-price-badge'>
                      <TrendingDown size={14} />
                      -37%
                    </span>
                  </div>
                  <div className='price-tracking-page__demo-alert'>
                    <Bell size={16} />
                    <span>{t('price_demo_alert', 'Price dropped below your target!')}</span>
                  </div>
                </div>
              </div>

              {/* Price Chart */}
              <div className='price-tracking-page__demo-chart'>
                <div className='price-tracking-page__demo-chart-header'>
                  <h4>{t('price_demo_chart_title', 'Price History')}</h4>
                  <div className='price-tracking-page__demo-chart-legend'>
                    <div className='price-tracking-page__demo-chart-legend-item'>
                      <div className='price-tracking-page__demo-chart-dot price-tracking-page__demo-chart-dot--target' />
                      <span>{t('price_target', 'Your Target: $200')}</span>
                    </div>
                  </div>
                </div>

                {/* Chart */}
                <div className='price-tracking-page__demo-chart-canvas'>
                  {/* Target line */}
                  <div className='price-tracking-page__demo-chart-target-line'>
                    <span className='price-tracking-page__demo-chart-target-label'>
                      <Target size={12} />
                      $200
                    </span>
                  </div>

                  {/* Bars */}
                  <div className='price-tracking-page__demo-chart-bars'>
                    {priceHistory.map((data, index) => (
                      <div
                        key={data.month}
                        className={`price-tracking-page__demo-chart-bar-wrapper ${
                          index === activeMonth ? 'active' : ''
                        }`}
                        onMouseEnter={() => setActiveMonth(index)}
                      >
                        <div className='price-tracking-page__demo-chart-tooltip'>${data.price}</div>
                        <div
                          className='price-tracking-page__demo-chart-bar'
                          style={{ height: `${(data.price / 300) * 100}%` }}
                        />
                        <span className='price-tracking-page__demo-chart-label'>{data.month}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className='price-tracking-page__features'>
          <div className='container'>
            <h2 className='price-tracking-page__section-title'>
              {t('price_features_title', 'Smart Price Tracking Features')}
            </h2>

            <div className='price-tracking-page__features-grid'>
              {features.map(feature => (
                <Card key={feature.title} className='price-tracking-page__feature-card'>
                  <div className='price-tracking-page__feature-icon'>
                    <feature.icon />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.desc}</p>
                  <ul className='price-tracking-page__feature-list'>
                    {feature.list.map(item => (
                      <li key={item}>
                        <Check />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className='price-tracking-page__how-it-works'>
          <div className='container'>
            <h2 className='price-tracking-page__section-title'>
              {t('price_how_it_works', 'How Price Tracking Works')}
            </h2>

            <div className='price-tracking-page__steps'>
              {howItWorksSteps.map(step => (
                <div key={step.step} className='price-tracking-page__step'>
                  <div className='price-tracking-page__step-number'>{step.step}</div>
                  <div className='price-tracking-page__step-content'>
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className='price-tracking-page__use-cases'>
          <div className='container'>
            <h2 className='price-tracking-page__section-title'>{t('price_perfect_for', 'Perfect For')}</h2>

            <div className='price-tracking-page__use-cases-grid'>
              {useCases.map(useCase => (
                <Card key={useCase.title} className='price-tracking-page__use-case'>
                  <div className='price-tracking-page__use-case-icon'>
                    <useCase.icon />
                  </div>
                  <h3>{useCase.title}</h3>
                  <p>{useCase.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className='price-tracking-page__cta'>
          <div className='container'>
            <div className='price-tracking-page__cta-content'>
              <h2>{t('price_cta_title', 'Start Saving Money Today')}</h2>
              <p>{t('price_cta_description', 'Join thousands who save $450+ per year with smart price tracking')}</p>
              <a href={URLS.CONTACT} className='price-tracking-page__cta-link'>
                <Button className='button-secondary'>
                  <TrendingDown />
                  {t('contact_us', 'Contact us')}
                  <ArrowRight />
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>
    </PublicWrapper>
  );
};

export default PriceTrackingPage;
